/**
 * Account.jsx — plan, fees, unlink.
 *
 * The break-even figure is stated plainly rather than buried. Under the free
 * tier the user pays 1% per trade, so $3,900/month of volume is where a $39
 * plan becomes cheaper. That is honest arithmetic which happens to convert
 * the users it should convert — and it is a statement of where plans are
 * managed, not a purchase CTA, which keeps it clear of Telegram's rules on
 * selling digital goods.
 */

import React, { useEffect, useState } from "react";
import * as api from "../lib/api";
import * as tg from "../lib/telegram";
import { C, money, shortAddr, Label, Button, Empty, Chip } from "../theme";

const BREAK_EVEN = 3900;

export default function Account({ onUnlinked }) {
  const [sub, setSub] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [bots, setBots] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, w, b] = await Promise.all([
          api.subscription().catch(() => null),
          api.balance().catch(() => null),
          api.listBots().catch(() => []),
        ]);
        setSub(s); setWallet(w);
        setBots(Array.isArray(b) ? b : b?.bots || []);
      } catch (e) { setErr(e.message); }
    })();
  }, []);

  const volume = bots.reduce((s, b) => s + Number(b.total_volume || 0), 0);
  const plan = (sub?.plan || sub?.tier || "free").toLowerCase();
  const isFree = plan === "free" || plan === "none";
  const feeRate = isFree ? 0.01 : 0;
  const feesPaid = volume * feeRate;

  const unlink = async () => {
    const ok = await tg.confirm(
      "Unlink this Telegram account? Your PolyEdge account is untouched.");
    if (!ok) return;
    setBusy(true);
    try {
      await api.unlink();
      tg.haptic.ok();
      onUnlinked();
    } catch (e) {
      tg.haptic.err(); setErr(e.message);
    } finally { setBusy(false); }
  };

  if (err && !sub && !wallet) {
    return <Empty><span style={{ color: C.red }}>{err}</span></Empty>;
  }

  return (
    <div style={{ padding: 14 }}>

      <div style={{ border: `0.5px solid ${C.border}`, background: C.bg1,
                    padding: 12, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "center", marginBottom: 9, gap: 8 }}>
          <span style={{ fontSize: 13, color: C.ink }}>
            {sub?.username || tg.unsafeUser()?.username || "Account"}
          </span>
          <Chip color={isFree ? C.dim : C.green}>{plan.toUpperCase()}</Chip>
        </div>
        <p style={{ fontSize: 11, color: C.dim, margin: 0,
                    wordBreak: "break-all" }}>
          {shortAddr(wallet?.address || wallet?.eoa_address)}
        </p>
      </div>

      <Label>THIS MONTH</Label>
      <div style={{ border: `0.5px solid ${C.border}`, background: C.bg1,
                    marginBottom: 14 }}>
        <Line label="Volume traded" value={money(volume, false)} />
        <Line label="Taker fee rate" value={isFree ? "1.0%" : "0%"} />
        <Line last label="Fees paid" value={money(feesPaid, false)}
              color={isFree ? C.amber : C.green} />
      </div>

      {isFree && (
        <div style={{ border: `0.5px solid ${C.border}`,
                      background: "rgba(245,166,35,0.04)", padding: 12,
                      marginBottom: 14 }}>
          <p style={{ fontSize: 11.5, color: C.ink, margin: "0 0 6px" }}>
            Break-even at {money(BREAK_EVEN, false)}/mo
          </p>
          <p style={{ fontSize: 10.5, color: C.dim, margin: 0, lineHeight: 1.65 }}>
            Below that, the 1% fee costs less than a $39 plan. Above it, a plan
            is cheaper. Plans are managed at polyedge.online.
          </p>
        </div>
      )}

      <Label>WHAT TELEGRAM CAN DO</Label>
      <div style={{ border: `0.5px solid ${C.border}`, background: C.bg1,
                    marginBottom: 14 }}>
        <Line label="View bots and P&amp;L" value="Yes" color={C.green} />
        <Line label="Create, stop, pause" value="Yes" color={C.green} />
        <Line label="Deposit" value="Yes" color={C.green} />
        <Line label="Withdraw" value="Web only" color={C.dim} />
        <Line last label="Export private key" value="Web only" color={C.dim} />
      </div>
      <p style={{ fontSize: 10.5, color: C.dim, lineHeight: 1.7,
                  margin: "0 0 18px" }}>
        A Telegram session has no password and no second factor, so anything
        that moves money out needs your account on the web.
      </p>

      {err && <p style={{ fontSize: 11.5, color: C.red, margin: "0 0 12px" }}>{err}</p>}

      <Button variant="danger" onClick={unlink} disabled={busy}>
        {busy ? "Unlinking…" : "Unlink Telegram"}
      </Button>
    </div>
  );
}

const Line = ({ label, value, color, last }) => (
  <div style={{ display: "flex", justifyContent: "space-between",
                padding: "10px 12px", gap: 10,
                borderBottom: last ? "none" : `0.5px solid rgba(245,166,35,0.12)` }}>
    <span style={{ fontSize: 12, color: C.dim }}>{label}</span>
    <span style={{ fontSize: 12, color: color || C.ink }}>{value}</span>
  </div>
);
